import { generatePath } from 'react-router-dom';

import { ResponsiveImage } from 'approot/shared/images/imgix-responsive.constants';
import type { LessonActivitiesFragment } from '../lesson-builder.graphql.generated';
import { getUserClassPath } from 'approot/classes/classes.apollo';
import { useCanAccessEdit } from '../permissions/activities-permissions.hooks';
import { InquisitiveActivitiesContentStatus } from 'global/graphql/scalars.type';
import ActivityBuilderConclusionSlide, {
  CompletionProps,
} from './activity-builder-conclusion-slide';
import { useFeatureCanAccessNativeFeedbackSurvey } from 'approot/feature-flags/use-feature-can-access-native-survey';
import {
  FeedbackFeature,
  CONTENT_OBJECT_TYPE_LESSON,
  CONTENT_OBJECT_TYPE_ASSESSMENT,
} from 'global/constants';
import type { Pluck } from 'lib/gql/pluck';

type FeedbackProps = {
  feature: FeedbackFeature;
  contentType:
    | typeof CONTENT_OBJECT_TYPE_LESSON
    | typeof CONTENT_OBJECT_TYPE_ASSESSMENT;
  contentId: number;
  classId: number;
  completionProps?: CompletionProps;
};

const ActivityBuilderConclusionView = ({
  focusQuestion,
  phenomena,
  contentName,
  contentSlug,
  responsiveImages,
  status,
  authorId,
  feedbackProps,
  completionProps,
}: {
  focusQuestion?: Pluck<LessonActivitiesFragment, 'focusQuestion'> | null;
  phenomena?: Pluck<LessonActivitiesFragment, 'phenomena'> | null;
  contentName: string;
  contentSlug: string;
  responsiveImages: ResponsiveImage[];
  status: InquisitiveActivitiesContentStatus;
  authorId: number;
  feedbackProps?: FeedbackProps;
  completionProps?: CompletionProps;
}) => {
  const canEditActivities = useCanAccessEdit(status);
  const canAccessNativeFeedbackSurvey =
    useFeatureCanAccessNativeFeedbackSurvey();

  const showFeedbackSurvey = canAccessNativeFeedbackSurvey && feedbackProps;

  return (
    <ActivityBuilderConclusionSlide
      exitLink={getUserClassPath(
        `${generatePath(contentSlug)}${canEditActivities ? '/edit' : ''}`
      )}
      exitLabel={
        !feedbackProps
          ? 'Exit'
          : `Exit ${
              feedbackProps?.contentType === CONTENT_OBJECT_TYPE_LESSON
                ? 'Lesson'
                : 'Assessment'
            }`
      }
      contentName={contentName}
      focusQuestion={focusQuestion}
      phenomena={phenomena}
      responsiveImages={responsiveImages}
      authorId={authorId}
      feedbackProps={
        showFeedbackSurvey
          ? {
              feature: feedbackProps.feature,
              contentName,
              contentType: feedbackProps.contentType,
              contentId: feedbackProps.contentId,
              classId: feedbackProps.classId,
            }
          : undefined
      }
      completionProps={
        completionProps
          ? {
              unitId: completionProps.unitId,
              isInPlanner: completionProps.isInPlanner,
              completion: completionProps.completion,
            }
          : undefined
      }
    />
  );
};

export default ActivityBuilderConclusionView;
